import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import axios from 'axios';
import {
  closestCenter,
  DndContext,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arraySwap,
  SortableContext,
  rectSwappingStrategy
} from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { useForm, useFormState } from 'react-final-form';
import PropTypes from 'prop-types';
import Icon from '../../../Icon';
import PhotoIcon from '../../../../public/static/images/new_ad/icons/icon-photo.svg';
import { ErrorField } from '../../../ui/Input/ErrorField';
import GalleryItem from '../components/GalleryItem';
import CONFIG from '../../../../config';
import {
  FORM_VALUES,
  MAX_PHOTOS_LENGTH
} from '../../../../utils/helpers/constants';

const errorHandler = error => {
  console.error(
    'POND API Error:',
    error.response ? error.response.data : error.message
  );
};

const Photos = ({ loading, setLoading }) => {
  const fileInputRef = useRef();
  const { change } = useForm();
  const { t, i18n } = useTranslation();
  const { values } = useFormState();
  const [isError, setError] = useState(false);
  const [loadingObject, setLoadingObject] = useState({});
  const [images, setImages] = useState(values[FORM_VALUES.images] || []);
  const [sourceObject, setSourceObject] = useState({});
  const [uploadsCounter, setUploadsCounter] = useState(0);
  const hasImages = images?.length > 0;

  const styles = classNames('item-modal__photo-upload', {
    'item-modal__photo-upload--has-images': hasImages || loading
  });

  useEffect(() => {
    const isLoading = Object.values(loadingObject).some(el => !!el);
    setLoading(isLoading);
  }, [loadingObject]);

  useEffect(() => {
    const isMockImages = images.some(el => el.thumbnail_url === null);
    if (images.length && !loading && !isMockImages) {
      change(FORM_VALUES.images, images);
    }
  }, [images, loading]);

  useEffect(() => {
    if (values[FORM_VALUES.images] === undefined && images.length) {
      setImages([]);
    }
    if (values[FORM_VALUES.images]?.length && !images.length) {
      setImages(values[FORM_VALUES.images]);
    }
  }, [values[FORM_VALUES.images]]);

  const sensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 5 }
    })
  );

  const onDragEnd = event => {
    const { active, over } = event;

    if (over?.id && active.id !== over.id) {
      const oldIndex = images.findIndex(el => el.id === active.id);
      const newIndex = images.findIndex(el => el.id === over.id);
      if (oldIndex >= 0 && newIndex >= 0) {
        const newArray = arraySwap(images, oldIndex, newIndex).filter(Boolean);
        setImages(newArray);
      }
    }
  };

  const handleAddPhoto = () => {
    fileInputRef.current?.click();
  };

  const catchErrors = i => err => {
    if (!axios.isCancel(err)) {
      // eslint-disable-next-line no-alert
      alert('Could not upload your photo. Please, try again.');
      setImages(prevState => prevState.filter(el => el.id !== i));
      errorHandler(err);
    }
  };

  const handleUpload = async (formData, i) => {
    const source = axios.CancelToken.source();
    setSourceObject(prevState => ({ ...prevState, [i]: source }));
    setLoadingObject(prevState => ({ ...prevState, [i]: true }));
    axios
      .post(`${CONFIG.photosBaseUrl}/pond`, formData, {
        params: { lang: i18n.language },
        cancelToken: source.token,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(response => {
        setImages(prevState =>
          prevState.map(el => (el.id === i ? response.data.photos[0] : el))
        );
      })
      .catch(catchErrors(i))
      .finally(() =>
        setLoadingObject(prevState => ({ ...prevState, [i]: false }))
      );
  };

  const handleFileInputChange = ({ target: { files } }) => {
    const counter = uploadsCounter + 1;
    setUploadsCounter(counter);
    if (files?.length) {
      const newFiles = Array.from(files);
      const newImages = newFiles.reduce((acc, file, i) => {
        const mockImage = {
          id: `${counter}-${i}`,
          thumbnail_url: null
        };
        acc.push(mockImage);
        const formData = new FormData();
        formData.append('images[]', file);
        handleUpload(formData, `${counter}-${i}`);
        return acc;
      }, []);
      setImages(prevState => [...prevState, ...newImages]);
    }
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();

    if (sourceObject[id]) {
      sourceObject[id].cancel('Operation canceled by the user.');
    }
    const updatedList = images?.filter(image => image?.id !== id);
    setImages(updatedList);
  };

  const handleRotate = (e, params) => {
    e.stopPropagation();
    axios
      .post(`${CONFIG.photosBaseUrl}/pond/rotate`, null, {
        params: {
          id: params.id,
          angle: params.angle,
          lang: i18n.language
        },
        headers: {
          Accept: 'application/json'
        }
      })
      .then(response => {
        const newValues = images.map(image =>
          image?.id === params.id ? response.data : image
        );
        setImages(newValues);
      })
      .catch(errorHandler);
  };

  return (
    <div className="filters__section" style={{ paddingBottom: '5px' }}>
      <div
        className="item-modal__block-header"
        style={{ marginBottom: '10px' }}
      >
        {t('new_ad.photos')}
      </div>
      <div className="item-modal__photo-wrapper">
        <DndContext
          sensors={sensors}
          onDragEnd={onDragEnd}
          collisionDetection={closestCenter}
        >
          <SortableContext items={images} strategy={rectSwappingStrategy}>
            {images.map((_, index) => {
              const indexWithCounter = `${uploadsCounter}-${index}`;
              const id = images[index]?.id || indexWithCounter;
              const url = images[index]?.thumbnail_url;
              return (
                <GalleryItem
                  id={id}
                  key={id}
                  url={url}
                  isLoading={loadingObject[id]}
                  handleDelete={e => handleDelete(e, id)}
                  handleRotateLeft={e => handleRotate(e, { id, angle: -90 })}
                  handleRotateRight={e => handleRotate(e, { id, angle: 90 })}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </div>
      <div
        tabIndex={0}
        role="button"
        className={styles}
        onClick={handleAddPhoto}
        onKeyPress={handleAddPhoto}
      >
        <Icon width={19} height={15} IconComponent={PhotoIcon} />
        <div className="item-modal__photo-text">{t('new_ad.add_photo')}</div>
        <input
          multiple
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />
      </div>
      <div className="item-modal__validation__counter">
        {t('new_ad.min_max_photos', {
          valueMin: values[FORM_VALUES.categoryValues].photosRequired,
          valueMax: MAX_PHOTOS_LENGTH
        })}
      </div>
      <ErrorField
        divider={false}
        setError={setError}
        field={FORM_VALUES.images}
        cypress="images-field-error"
        margin={isError ? '10px' : 0}
      />
    </div>
  );
};

Photos.propTypes = {
  loading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired
};

export default Photos;
